/**
 * WHAT THE SHOP IS CURRENTLY OFFERED, IN ONE PLACE.
 *
 * The canonical vocabulary (`AUTHORITY_ROLES`, `WORK_ROLES`) is the set of
 * values the system can REPRESENT. That is not the same question as which
 * values a shop is currently offered when somebody opens a role editor, and
 * conflating the two is how four surfaces ended up disagreeing:
 *
 *   Team Management member editor   every WorkRole, including Dispatcher/Parts
 *   Team Management invite          the same list
 *   Shop Profile → technician edit  Apprentice / Technician / Foreman,
 *                                   written to `technicians.role`
 *   Beta / onboarding               the full WorkRole list again
 *
 * Apprentice and Foreman are not work roles at all — they are skill titles that
 * predate the role model, and offering them beside "Technician" asked the
 * operator to choose between a job and a seniority band.
 *
 * ── WHAT THIS MODULE IS NOT ────────────────────────────────────────────────
 *
 * It does NOT narrow the canonical enums, and it must never be used to filter a
 * value on the way INTO storage. Dispatcher and Parts remain fully supported:
 * stored assignments keep working, `WORK_ROLE_FAMILIES` still knows them, the
 * server still accepts them, and a membership that holds one keeps it through
 * an unrelated save. They are simply not offered as a new choice today.
 *
 * The distinction matters because the failure mode is silent. A UI that renders
 * a filtered list and then saves "what is checked" deletes every role it chose
 * not to draw. Selection state must always be reconstructed from the CANONICAL
 * list, with this one used only to decide what to paint.
 */
import { WORK_ROLES, type AuthorityRole, type WorkRole } from './multiRole';

/**
 * THE NEUTRAL ACCESS CHOICE — an ordinary shop-floor employee.
 *
 * STAFF is not a job and not an absence of one. It is what somebody's ACCESS is
 * when they administer nothing, and it is the ordinary selection for most of a
 * shop's people. A technician is `STAFF` + work role TECHNICIAN.
 */
export const NEUTRAL_AUTHORITY: AuthorityRole = 'STAFF';

/**
 * The ELEVATED access levels a shop hands out. One value, mutually exclusive.
 *
 * ── WHY THIS IS NOT CALLED "SHOP_OFFERED_AUTHORITIES" ──────────────────────
 *
 * It was, for exactly one commit, and the name was wrong in a way worth
 * recording. `[OWNER, MANAGER]` is not the set of Access choices a shop makes —
 * it is the set of choices that grant administrative power. The ordinary choice
 * is STAFF, and a constant named "the offered authorities" that omits it invites
 * a future editor to render `SHOP_OFFERED_AUTHORITIES.map(...)` and quietly
 * delete Staff from the invitation UI. Nothing had consumed it yet; the rename
 * is what stops that from ever being a tempting one-liner.
 *
 * ── AND WHY NO UI SHOULD RENDER THIS DIRECTLY ─────────────────────────────
 *
 * The real offering is CAPABILITY-DERIVED: `assignableAuthoritiesFor(actor)`
 * returns what THIS actor may grant, which is how a MANAGER is prevented from
 * minting an OWNER. A flat constant cannot express that, and a second source of
 * authority truth is precisely the class of drift this module exists to end.
 *
 * Use this to ask "is this an elevated access level" — not to build a dropdown.
 */
export const SHOP_OFFERED_ELEVATED_AUTHORITIES: readonly AuthorityRole[] = [
  'OWNER',
  'MANAGER',
] as const;

/** Does this access level grant administrative power over the shop? */
export const isElevatedAuthority = (r: AuthorityRole): boolean =>
  SHOP_OFFERED_ELEVATED_AUTHORITIES.includes(r);

/**
 * The jobs a shop assigns today. Additive — a person may hold several.
 *
 * `DISPATCHER` and `PARTS` are intentionally not here. See the note above:
 * absent from the OFFERING, present in the MODEL.
 */
export const SHOP_OFFERED_WORK_ROLES: readonly WorkRole[] = [
  'SERVICE_ADVISOR',
  'TECHNICIAN',
  'DETAIL_SPECIALIST',
] as const;

/** Roles the system supports but the shop is not currently offered. */
export const NON_OFFERED_WORK_ROLES: readonly WorkRole[] = WORK_ROLES.filter(
  (r) => !SHOP_OFFERED_WORK_ROLES.includes(r),
);

export const isOfferedWorkRole = (r: WorkRole): boolean =>
  SHOP_OFFERED_WORK_ROLES.includes(r);

/**
 * Which roles should a role editor DRAW for this member?
 *
 * The currently offered set, plus anything this person already holds that is no
 * longer offered. A held Dispatcher is shown — as something they have, not as
 * something to hand out — because hiding a role somebody actually holds makes
 * the editor lie about them, and that is how it gets dropped.
 */
export function workRolesToRender(held: readonly WorkRole[]): WorkRole[] {
  const heldButNotOffered = WORK_ROLES.filter(
    (r) => held.includes(r) && !SHOP_OFFERED_WORK_ROLES.includes(r),
  );
  return WORK_ROLES.filter(
    (r) => SHOP_OFFERED_WORK_ROLES.includes(r) || heldButNotOffered.includes(r),
  );
}

/**
 * The legacy `technicians.role` titles.
 *
 * Retained so historical rows keep rendering, and explicitly NOT operational
 * roles. A Foreman is a technician with a seniority title; the operational
 * question "what work may this person be assigned" is answered by work roles
 * and `WORK_ROLE_FAMILIES`, never by this string.
 *
 * ── WHY "SPECIALIST" IS HERE, AND WHY IT ALMOST WAS NOT ────────────────────
 *
 * The authority for this list is the column's own CHECK constraint, written in
 * the very first `technicians` migration (20251023235313):
 *
 *     role TEXT NOT NULL CHECK (role IN
 *       ('Technician', 'Foreman', 'Specialist', 'Apprentice'))
 *
 * Four values, and Specialist has been storable — and writable from
 * `TechnicianEditor` — since the table existed. An earlier draft of this
 * constant was built from the `TechnicianForm` dropdown instead, which offers
 * three, and that produced a real defect: a roster row stored as `Specialist`
 * loaded into a form whose zod enum did not admit it, so the picker rendered
 * blank and the save was refused on a field nobody had touched. The list now
 * mirrors the constraint, which is the only thing that can say what the column
 * holds.
 *
 * ── AND WHY IT IS NOT `DETAIL_SPECIALIST` ──────────────────────────────────
 *
 * They share six letters and nothing else. `Specialist` is a SENIORITY TITLE
 * on the shop's roster row — a technician with a specialism, historically used
 * for a diesel or transmission specialist. `DETAIL_SPECIALIST` is an
 * operational WORK ROLE in `membership_work_roles` that makes somebody
 * eligible for DETAIL-family work and, deliberately, for nothing mechanical.
 *
 * Reading one as the other would go wrong in the worst available direction:
 * a mechanical specialist would be reclassified as a detailer and lose every
 * mechanical assignment, or a detailer would be handed a brake job. Nothing
 * may map between these two vocabularies, and `isLegacyTechnicianTitle`
 * exists so a caller can ask "is this a roster title" without being tempted to
 * compare it to a work role.
 */
export const LEGACY_TECHNICIAN_TITLES: readonly string[] = [
  'Apprentice',
  'Technician',
  'Specialist',
  'Foreman',
] as const;

/**
 * The roster titles that describe SENIORITY ALONE and name no canonical role.
 *
 * `Technician` is excluded because it is the one legacy title that is also
 * spelled like a work role — a coincidence, not a mapping.
 */
export const LEGACY_SENIORITY_ONLY_TITLES: readonly string[] =
  LEGACY_TECHNICIAN_TITLES.filter((t) => t !== 'Technician');

export const isLegacyTechnicianTitle = (v: string | null | undefined): boolean =>
  !!v && LEGACY_TECHNICIAN_TITLES.includes(v);
